// BusinessEvaluator.js
import React, { useState, useEffect } from "react";
import businessSummaryService from "../../services/SummaryService";
import { CiCalculator1 } from "react-icons/ci";
import { MdError, MdAssessment } from "react-icons/md";
import { FaCheck, FaChartLine, FaDollarSign } from "react-icons/fa6";
import { FaUndo, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
const BusinessEvaluator = ({
  businessId = null,
  onSuccess = null,
  setShowModal,
}) => {
    const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    service: "",
    revenue: "",
    expenses: "",
    equipment: "",
    patients: "",
    seniority: "",
    employees: "",
    city: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [businessValuation, setBusinessValuation] = useState(null);
  const [isEvaluated, setIsEvaluated] = useState(false);

  // Load existing data if businessId is provided
  useEffect(() => {
    if (businessId) {
      loadBusinessSummary(businessId);
      setIsEditing(true);
    }
  }, [businessId]);

  const loadBusinessSummary = async (id) => {
    try {
      setLoading(true);
      const response = await businessSummaryService.getBusinessSummary(id);
      if (response.data && response.data.result) {
        setFormData({
          service: response.data.result.service || "",
          revenue: response.data.result.revenue || "",
          expenses: response.data.result.expenses || "",
          equipment: response.data.result.equipment || "",
          patients: response.data.result.patients || "",
          seniority: response.data.result.seniority || "",
          employees: response.data.result.employees || "",
          city: response.data.result.city || "",
        });
      }
    } catch (error) {
      console.error("Error loading business summary:", error);
      setErrors({ general: t("hfailedloadbusiness") });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.service.trim()) {
      newErrors.service = "Service/Department is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // Numeric validations
    if (
      formData.revenue &&
      (isNaN(formData.revenue) || parseFloat(formData.revenue) < 0)
    ) {
      newErrors.revenue = "Revenue must be a valid positive number";
    }

    if (
      formData.expenses &&
      (isNaN(formData.expenses) || parseFloat(formData.expenses) < 0)
    ) {
      newErrors.expenses = "Expenses must be a valid positive number";
    }

    if (
      formData.equipment &&
      (isNaN(formData.equipment) || parseFloat(formData.equipment) < 0)
    ) {
      newErrors.equipment = "Equipment cost must be a valid positive number";
    }

    if (
      formData.patients &&
      (isNaN(formData.patients) || parseInt(formData.patients) < 0)
    ) {
      newErrors.patients = "Patient count must be a valid positive number";
    }

    if (
      formData.seniority &&
      (isNaN(formData.seniority) || parseInt(formData.seniority) < 0)
    ) {
      newErrors.seniority =
        "Years in operation must be a valid positive number";
    }

    if (
      formData.employees &&
      (isNaN(formData.employees) || parseInt(formData.employees) < 0)
    ) {
      newErrors.employees = "Employee count must be a valid positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSubmitSuccess(false);

    try {
      // Prepare data for API with correct field names
      const apiData = {
        service: formData.service.trim(),
        revenue: formData.revenue ? parseFloat(formData.revenue) : 0,
        expenses: formData.expenses ? parseFloat(formData.expenses) : 0,
        equipment: formData.equipment ? parseFloat(formData.equipment) : 0,
        patients: formData.patients ? parseInt(formData.patients) : 0,
        seniority: formData.seniority ? parseInt(formData.seniority) : 0,
        employees: formData.employees ? parseInt(formData.employees) : 0,
        city: formData.city.trim(),
      };

      let response;
      if (isEditing && businessId) {
        response = await businessSummaryService.updateBusinessSummary(
          businessId,
          apiData
        );
      } else {
        response = await businessSummaryService.submitBusinessSummary(apiData);
      }

      setSubmitSuccess(true);
      setIsEvaluated(true);

      // Store business valuation data if available
      if (response.data) {
        setBusinessValuation({
          estimated_value: response.data.result.estimated_value || 0,
          multiplier: response.data.result.multiplier || 0,
        });
      }

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error("Error submitting business evaluation:", error);

      // Handle specific error responses
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({
          general: "Failed to evaluate business. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      service: "",
      revenue: "",
      expenses: "",
      equipment: "",
      patients: "",
      seniority: "",
      employees: "",
      city: "",
    });
    setErrors({});
    setSubmitSuccess(false);
    setBusinessValuation(null);
    setIsEvaluated(false);
  };

  const startNewEvaluation = () => {
    resetForm();
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-2"
      style={{
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 1050,
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12">
            <div
              className="card shadow-lg border-0"
              style={{
                borderRadius: "16px",
                overflow: "hidden",
                maxHeight: "98vh",
                height: "98vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Compact Header */}
              <div
                className="card-header text-white d-flex justify-content-between align-items-center"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  padding: "1rem 1.5rem",
                  minHeight: "auto",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaChartLine className="me-2" size={20} />
                  <div>
                    <h5 className="mb-0 fw-bold">{t("businessEvaluator")}</h5>
                    <small
                      className="opacity-75"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {t("hgetinstant")}
                    </small>
                  </div>
                </div>
                <button
                  className="btn btn-link text-white p-0"
                  onClick={() => setShowModal(false)}
                  style={{ fontSize: "1.2rem", textDecoration: "none" }}
                >
                  <FaTimes />
                </button>
              </div>

              <div
                className="card-body p-3"
                style={{
                  backgroundColor: "#f8f9fa",
                  overflowY: "auto",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Compact Alert Messages */}
                {submitSuccess && (
                  <div
                    className="alert alert-success border-0 shadow-sm mb-2 py-2"
                    style={{ borderRadius: "8px", fontSize: "0.875rem" }}
                  >
                    <div className="d-flex align-items-center">
                      <FaCheck className="me-2 text-success" size={14} />
                        <strong>{t("hevaluationComplete")}!</strong>
                    </div>
                  </div>
                )}

                {errors.general && (
                  <div
                    className="alert alert-danger border-0 shadow-sm mb-2 py-2"
                    style={{ borderRadius: "8px", fontSize: "0.875rem" }}
                  >
                    <div className="d-flex align-items-center">
                      <MdError className="me-2" size={14} />
                      <strong>{t("herrors")}:</strong> {errors.general}
                    </div>
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="flex-grow-1 d-flex flex-column"
                >
                  {/* Two Column Layout for Form and Results */}
                  <div className="row g-3 flex-grow-1">
                    {/* Form Column */}
                    <div
                      className={`${
                        businessValuation && isEvaluated ? "col-lg-8" : "col-12"
                      }`}
                    >
                      <div className="row g-2">
                        {/* Service/Department */}
                        <div className="col-md-6">
                          <label
                            htmlFor="service"
                            className="form-label fw-semibold small"
                          >
                            {t("hserviceDepartment")}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control form-control-sm ${
                              errors.service ? "is-invalid" : ""
                            }`}
                            id="service"
                            name="service"
                            value={formData.service}
                            onChange={handleInputChange}
                            placeholder="e.g., Cardiology, Emergency"
                            disabled={loading || isEvaluated}
                            style={{ borderRadius: "6px" }}
                          />
                          {errors.service && (
                            <div className="invalid-feedback small">
                              {errors.service}
                            </div>
                          )}
                        </div>

                        {/* City */}
                        <div className="col-md-6">
                          <label
                            htmlFor="city"
                            className="form-label fw-semibold small"
                          >
                            {t("signUpCityInput")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control form-control-sm ${
                              errors.city ? "is-invalid" : ""
                            }`}
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            placeholder="Enter city name"
                            disabled={loading || isEvaluated}
                            style={{ borderRadius: "6px" }}
                          />
                          {errors.city && (
                            <div className="invalid-feedback small">
                              {errors.city}
                            </div>
                          )}
                        </div>

                        {/* Revenue */}
                        <div className="col-md-6">
                          <label
                            htmlFor="revenue"
                            className="form-label fw-semibold small"
                          >
                            {t("hannualRevenue")}
                          </label>
                          <div className="input-group input-group-sm">
                            <span
                              className="input-group-text bg-light"
                              style={{ borderRadius: "6px 0 0 6px" }}
                            >
                              <FaDollarSign
                                className="text-success"
                                size={12}
                              />
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`form-control ${
                                errors.revenue ? "is-invalid" : ""
                              }`}
                              id="revenue"
                              name="revenue"
                              value={formData.revenue}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              disabled={loading || isEvaluated}
                              style={{ borderRadius: "0 6px 6px 0" }}
                            />
                            {errors.revenue && (
                              <div className="invalid-feedback small">
                                {errors.revenue}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expenses */}
                        <div className="col-md-6">
                          <label
                            htmlFor="expenses"
                            className="form-label fw-semibold small"
                          >
                            {t("hannualExpenses")}
                          </label>
                          <div className="input-group input-group-sm">
                            <span
                              className="input-group-text bg-light"
                              style={{ borderRadius: "6px 0 0 6px" }}
                            >
                              <FaDollarSign className="text-danger" size={12} />
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`form-control ${
                                errors.expenses ? "is-invalid" : ""
                              }`}
                              id="expenses"
                              name="expenses"
                              value={formData.expenses}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              disabled={loading || isEvaluated}
                              style={{ borderRadius: "0 6px 6px 0" }}
                            />
                            {errors.expenses && (
                              <div className="invalid-feedback small">
                                {errors.expenses}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Equipment */}
                        <div className="col-md-6">
                          <label
                            htmlFor="equipment"
                            className="form-label fw-semibold small"
                          >
                            {t("hequipmentInvestment")}
                          </label>
                          <div className="input-group input-group-sm">
                            <span
                              className="input-group-text bg-light"
                              style={{ borderRadius: "6px 0 0 6px" }}
                            >
                              <FaDollarSign className="text-info" size={12} />
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className={`form-control ${
                                errors.equipment ? "is-invalid" : ""
                              }`}
                              id="equipment"
                              name="equipment"
                              value={formData.equipment}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              disabled={loading || isEvaluated}
                              style={{ borderRadius: "0 6px 6px 0" }}
                            />
                            {errors.equipment && (
                              <div className="invalid-feedback small">
                                {errors.equipment}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Patients */}
                        <div className="col-md-6">
                          <label
                            htmlFor="patients"
                            className="form-label fw-semibold small"
                          >
                            {t("hpatientcount")}
                          </label>
                          <input
                            type="number"
                            min="0"
                            className={`form-control form-control-sm ${
                              errors.patients ? "is-invalid" : ""
                            }`}
                            id="patients"
                            name="patients"
                            value={formData.patients}
                            onChange={handleInputChange}
                            placeholder="Number of patients"
                            disabled={loading || isEvaluated}
                            style={{ borderRadius: "6px" }}
                          />
                          {errors.patients && (
                            <div className="invalid-feedback small">
                              {errors.patients}
                            </div>
                          )}
                        </div>

                        {/* Years in Operation */}
                        <div className="col-md-6">
                          <label
                            htmlFor="seniority"
                            className="form-label fw-semibold small"
                          >
                            {t("hyearsOperation")}
                          </label>
                          <input
                            type="number"
                            min="0"
                            className={`form-control form-control-sm ${
                              errors.seniority ? "is-invalid" : ""
                            }`}
                            id="seniority"
                            name="seniority"
                            value={formData.seniority}
                            onChange={handleInputChange}
                            placeholder="Years of operation"
                            disabled={loading || isEvaluated}
                            style={{ borderRadius: "6px" }}
                          />
                          {errors.seniority && (
                            <div className="invalid-feedback small">
                              {errors.seniority}
                            </div>
                          )}
                        </div>

                        {/* Employees */}
                        <div className="col-md-6">
                          <label
                            htmlFor="employees"
                            className="form-label fw-semibold small"
                          >
                            {t("hemployeeCount")}
                          </label>
                          <input
                            type="number"
                            min="0"
                            className={`form-control form-control-sm ${
                              errors.employees ? "is-invalid" : ""
                            }`}
                            id="employees"
                            name="employees"
                            value={formData.employees}
                            onChange={handleInputChange}
                            placeholder="Number of employees"
                            disabled={loading || isEvaluated}
                            style={{ borderRadius: "6px", paddingLeft: "20px" }}
                          />
                          {errors.employees && (
                            <div className="invalid-feedback small">
                              {errors.employees}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Results Column */}
                    {businessValuation && isEvaluated && (
                      <div className="col-lg-4">
                        <div
                          className="card border-0 shadow-sm h-100"
                          style={{
                            borderRadius: "12px",
                            background:
                              "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                          }}
                        >
                          <div className="card-header bg-transparent border-0 pb-2">
                            <div className="d-flex align-items-center">
                              <MdAssessment
                                className="me-2 text-primary"
                                size={18}
                              />
                              <h6 className="mb-0 fw-bold text-primary">
                                {t("hevaluation")}
                              </h6>
                            </div>
                          </div>
                          <div className="card-body pt-2">
                            <div className="mb-3">
                              <div
                                className="p-3 text-center rounded-3"
                                style={{
                                  backgroundColor: "rgba(40, 167, 69, 0.1)",
                                }}
                              >
                                <CiCalculator1
                                  className="text-success mb-1"
                                  size={24}
                                />
                                <div className="text-muted small">
                                  {t("hestimatedValue")}
                                </div>
                                <div className="h5 mb-0 fw-bold text-success">
                                  $
                                  {businessValuation.estimated_value.toLocaleString(
                                    "en-US",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div
                                className="p-3 text-center rounded-3"
                                style={{
                                  backgroundColor: "rgba(0, 123, 255, 0.1)",
                                }}
                              >
                                <FaChartLine
                                  className="text-primary mb-1"
                                  size={24}
                                />
                                <div className="text-muted small">
                                  {t("hrevenuemultiplier")}
                                </div>
                                <div className="h5 mb-0 fw-bold text-primary">
                                  {businessValuation.multiplier.toFixed(2)}x
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Compact Form Actions */}
                  <div className="d-flex justify-content-between mt-3 pt-2 border-top">
                    {isEvaluated ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary px-3"
                        onClick={startNewEvaluation}
                        style={{ borderRadius: "6px" }}
                      >
                        <FaUndo className="me-1" size={14} />
                        {t("hnewEvaluation")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-secondary px-3"
                        onClick={resetForm}
                        disabled={loading}
                        style={{ borderRadius: "6px" }}
                      >
                        <FaUndo className="me-1" size={14} />
                        {t("hresetform")}
                      </button>
                    )}

                    {!isEvaluated && (
                      <button
                        type="submit"
                        className="btn px-4"
                        disabled={loading}
                        style={{
                          borderRadius: "6px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                          color: "white",
                        }}
                      >
                        {loading && (
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            style={{ width: "1rem", height: "1rem" }}
                          ></span>
                        )}
                        {loading ? (
                          t("hevaluating")
                        ) : (
                          <>
                            <CiCalculator1 className="me-1" size={16} />
                            {isEditing
                              ? t("hupdateEvaluation")
                              : t("hevaluateBusiness")}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessEvaluator;
